import type {
  ComponentRendering,
  HtmlElementRendering,
  PlaceholdersData,
  RouteData,
} from '@sitecore-jss/sitecore-jss/layout';

interface ErrorDetails {
  missingDatasource?: boolean;
  missingChildren?: boolean;
}

type ErrorRendering = ComponentRendering &
  ErrorDetails & {
    validatedComponentName: string;
  };

type ValidatableRendering = RouteData | ComponentRendering | HtmlElementRendering | null;

// Route validation constants and helpers
const NO_DATASOURCE_REQ_COMPONENTS = new Set([
  'BlogListing',
  'BlogPost',
  'BookSpaceWidget',
  'BookTableWidget',
  'OpenTableWidget',
  'CookieDeclaration',
  'FixedPromoMessage',
  'FormContainer',
  'HeroCarousel',
  'InteractiveMap',
  'OpenTabletWidget',
  'PromoCarousel',
  'PubInfoBar',
  'RegularEventsList',
  'SitemapBlock',
  'SportEventList',
  'SubNavigation',
  'BreadcrumbsBar',
  'PubIntroduction',
  'BusinessDevelopmentManagerCard',
  'ImportantInformationAccordion',
  'BookingWidget',
  'StickyBookingBlock',
  'AwardsCarousel',
  'RelatedContent',
]);

const NO_CHILD_REQ_COMPONENTS = new Set(['SportEventList', 'IntroductionItem']);

const isComponentRendering = (rendering: ValidatableRendering): rendering is ComponentRendering =>
  !!rendering && 'componentName' in rendering && !!rendering.componentName;

const ErrorComponent = (validatedComponentName: string, args: ErrorDetails): ErrorRendering => ({
  componentName: 'Error Component',
  validatedComponentName,
  ...args,
});

export function validateRoute(route: null): null;
export function validateRoute(route: RouteData): RouteData;
export function validateRoute(route: RouteData | null): RouteData | null;
export function validateRoute(route: ComponentRendering): ComponentRendering;
export function validateRoute(route: HtmlElementRendering): HtmlElementRendering;
export function validateRoute(
  route: ComponentRendering | HtmlElementRendering
): ComponentRendering | HtmlElementRendering;
export function validateRoute(route: ValidatableRendering): ValidatableRendering {
  // Check if route object is a component and has datasource defined
  if (
    isComponentRendering(route) &&
    !route.dataSource &&
    !NO_DATASOURCE_REQ_COMPONENTS.has(route.componentName)
  ) {
    return ErrorComponent(route.componentName, { missingDatasource: true });
  }

  // If the route object has a placeholder defined, check if the placeholder contains at least one valid component.
  // If so, validate the children.
  if (route && 'placeholders' in route && route.placeholders) {
    const placeholders: PlaceholdersData = Object.fromEntries(
      Object.entries(route.placeholders).map(([placeholderKey, placeholderItems]) => {
        if (
          !placeholderItems.some((child) => isComponentRendering(child)) &&
          !placeholderKey.startsWith('jss-') &&
          !(isComponentRendering(route) && NO_CHILD_REQ_COMPONENTS.has(route.componentName))
        ) {
          return [
            placeholderKey,
            [
              ...placeholderItems,
              ErrorComponent(isComponentRendering(route) ? route.componentName : placeholderKey, {
                missingChildren: true,
              }),
            ],
          ];
        }

        return [placeholderKey, placeholderItems.map((child) => validateRoute(child))];
      })
    );

    return { ...route, placeholders };
  }

  return route;
}
